import type { ReactNode } from "react";
import { InfoTip } from "@client/components/InfoTip";

interface PanelProps {
  title?: ReactNode;
  description?: ReactNode;
  /**
   * The explanatory half of what `description` used to carry, folded behind an
   * icon beside the title. `description` keeps whatever is worth reading
   * without asking — a figure, a count, a break-even year.
   */
  info?: ReactNode;
  /** The accessible name for the `info` trigger; falls back to the title. */
  infoLabel?: string;
  /** Controls for the panel as a whole; sits opposite the title. */
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** An anchor target, for a warning that links to what it is about. */
  id?: string;
  /** Drop the card chrome — for a panel that is already inside one. */
  bare?: boolean;
}

/** A titled section on the page. The card styling lives in globals.css. */
export function Panel({
  title,
  description,
  info,
  infoLabel,
  actions,
  children,
  className = "",
  id,
  bare = false,
}: PanelProps) {
  return (
    <section id={id} className={`${bare ? "" : "island-shell"} flex flex-col gap-4 ${className}`}>
      {(title || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            {title && (
              <h2 className="flex items-center gap-1.5 text-base font-bold text-ink">
                {title}
                {info && (
                  <InfoTip label={infoLabel ?? (typeof title === "string" ? title : "this panel")}>
                    {info}
                  </InfoTip>
                )}
              </h2>
            )}
            {description && <p className="max-w-prose text-sm text-ink-soft">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}
